import { z } from 'zod';
import { idSchema, withAudit } from '../model/commun.js';
const centimetresSchema = z
    .number({ error: 'Une mesure en centimètres est attendue.' })
    .transform((valeur) => Math.round(valeur * 10) / 10);
const largeurSchema = z
    .number({ error: 'Une largeur en centimètres est attendue.' })
    .positive('La largeur doit être positive.')
    .max(10_000, 'La largeur ne peut pas dépasser 10 000 cm.')
    .pipe(centimetresSchema)
    .pipe(z.number().positive('La mesure arrondie doit rester positive.'));
const hauteurSchema = z
    .number({ error: 'Une hauteur en centimètres est attendue.' })
    .positive('La hauteur doit être positive.')
    .max(2_000, 'La hauteur ne peut pas dépasser 2 000 cm.')
    .pipe(centimetresSchema)
    .pipe(z.number().positive('La mesure arrondie doit rester positive.'));
const coordonneeZoneSchema = z
    .number({ error: 'Une position en centimètres est attendue.' })
    .nonnegative('La position doit être positive ou nulle.')
    .pipe(centimetresSchema);
const revisionSchema = z
    .int({ error: 'Une révision entière est attendue.' })
    .nonnegative('La révision doit être positive ou nulle.');
const nomSchema = z
    .string({ error: 'Un nom est attendu.' })
    .trim()
    .min(1, 'Le nom est obligatoire.')
    .max(120, 'Le nom ne peut pas dépasser 120 caractères.');
const axeSchema = z
    .number({ error: 'Un axe en centimètres est attendu.' })
    .positive('L’axe doit être positif.')
    .max(400, 'L’axe ne peut pas dépasser 400 cm.')
    .pipe(centimetresSchema)
    .pipe(z.number().positive('La mesure arrondie doit rester positive.'));
export const tailleAccrochageSchema = z.object({
    largeurCm: largeurSchema,
    hauteurCm: hauteurSchema,
});
export const zoneSchema = z.object({
    _id: idSchema,
    nature: z.enum(['porte', 'fenetre', 'radiateur', 'prise', 'autre'], {
        error: 'La nature de la zone est invalide.',
    }),
    libelle: z
        .string({ error: 'Un libellé est attendu.' })
        .max(80, 'Le libellé ne peut pas dépasser 80 caractères.')
        .optional(),
    xCm: coordonneeZoneSchema,
    yCm: coordonneeZoneSchema,
    ...tailleAccrochageSchema.shape,
});
const zoneSaisieSchema = zoneSchema.partial({ _id: true });
const murSaisieSchema = z
    .object({
    _id: idSchema.optional(),
    nom: z
        .string({ error: 'Un nom de mur est attendu.' })
        .trim()
        .min(1, 'Le nom du mur est obligatoire.')
        .max(80, 'Le nom du mur ne peut pas dépasser 80 caractères.'),
    ordre: z
        .int({ error: 'Un ordre entier est attendu.' })
        .min(0, 'L’ordre doit être positif ou nul.')
        .max(49, 'L’ordre ne peut pas dépasser 49.'),
    ...tailleAccrochageSchema.shape,
    zones: z
        .array(zoneSaisieSchema, { error: 'Une liste de zones est attendue.' })
        .max(50, 'Au plus 50 zones par mur.')
        .default([]),
})
    .superRefine((mur, contexte) => {
    mur.zones.forEach((zone, indice) => {
        // La comparaison en dixièmes évite un faux débordement dû aux flottants.
        if (Math.round(zone.xCm * 10) + Math.round(zone.largeurCm * 10) >
            Math.round(mur.largeurCm * 10) ||
            Math.round(zone.yCm * 10) + Math.round(zone.hauteurCm * 10) > Math.round(mur.hauteurCm * 10)) {
            contexte.addIssue({
                code: 'custom',
                path: ['zones', indice],
                message: 'La zone doit tenir entièrement dans son mur.',
            });
        }
    });
});
export const murSchema = murSaisieSchema.safeExtend({
    _id: idSchema,
    zones: z
        .array(zoneSchema, { error: 'Une liste de zones est attendue.' })
        .max(50, 'Au plus 50 zones par mur.')
        .default([]),
});
function nomsUniques(murs, contexte) {
    const noms = new Set();
    murs.forEach((mur, indice) => {
        if (noms.has(mur.nom))
            contexte.addIssue({
                code: 'custom',
                path: [indice, 'nom'],
                message: 'Les noms des murs doivent être uniques.',
            });
        noms.add(mur.nom);
    });
}
const espaceContenuSchema = z.object({
    nom: nomSchema,
    nature: z.enum(['salle', 'stand', 'hors-les-murs'], {
        error: 'La nature de l’espace est invalide.',
    }),
    lieuId: idSchema.nullable().default(null),
    murs: z
        .array(murSaisieSchema, { error: 'Une liste de murs est attendue.' })
        .max(50, 'Au plus 50 murs par espace.')
        .superRefine(nomsUniques)
        .default([]),
});
export const espaceCreationSchema = espaceContenuSchema;
export const espaceMiseAJourSchema = espaceContenuSchema.extend({ revision: revisionSchema });
export const espaceSchema = withAudit(espaceContenuSchema.extend({
    _id: idSchema,
    murs: z
        .array(murSchema, { error: 'Une liste de murs est attendue.' })
        .max(50, 'Au plus 50 murs par espace.')
        .superRefine(nomsUniques),
    archive: z.boolean().default(false),
    archiveLe: z.date().nullable().default(null),
    revision: revisionSchema,
}));
// Une œuvre peut déborder du mur (c'est une alerte, pas un refus), mais pas
// sortir de toute mesure plausible.
const positionPlacementSchema = z
    .number({ error: 'Une position en centimètres est attendue.' })
    .min(-10_000, 'La position ne peut pas être inférieure à −10 000 cm.')
    .max(10_000, 'La position ne peut pas dépasser 10 000 cm.')
    .pipe(centimetresSchema);
const decalageCrochetSchema = z
    .number({ error: 'Un décalage en centimètres est attendu.' })
    .nonnegative('Le décalage du crochet doit être positif ou nul.')
    .max(2_000, 'Le décalage du crochet ne peut pas dépasser 2 000 cm.')
    .pipe(centimetresSchema);
export const placementSchema = z.object({
    oeuvreId: idSchema,
    murId: idSchema,
    xCm: positionPlacementSchema,
    yCm: positionPlacementSchema,
    cadre: tailleAccrochageSchema.optional(),
    tailleProvisoire: tailleAccrochageSchema.optional(),
    decalageCrochetCm: decalageCrochetSchema.default(0),
});
const placementsSchema = z
    .array(placementSchema, { error: 'Une liste de placements est attendue.' })
    .max(500, 'Au plus 500 œuvres par plan.')
    .superRefine((placements, contexte) => {
    const oeuvres = new Set();
    placements.forEach((placement, indice) => {
        if (oeuvres.has(placement.oeuvreId))
            contexte.addIssue({
                code: 'custom',
                path: [indice, 'oeuvreId'],
                message: 'placement.oeuvre_en_double',
            });
        oeuvres.add(placement.oeuvreId);
    });
});
const etatSchema = z.enum(['brouillon', 'valide'], { error: 'L’état du plan est invalide.' });
export const planAccrochageCreationSchema = z.object({
    expositionId: idSchema,
    espaceId: idSchema,
    nom: nomSchema,
    axeCm: axeSchema.default(155),
});
export const planAccrochageMiseAJourSchema = z.object({
    revision: revisionSchema,
    nom: nomSchema.optional(),
    axeCm: axeSchema.optional(),
    etat: etatSchema.optional(),
    placements: placementsSchema,
});
export const planAccrochageSchema = withAudit(planAccrochageCreationSchema.extend({
    _id: idSchema,
    etat: etatSchema.default('brouillon'),
    placements: placementsSchema.default([]),
    appliqueLe: z.date().nullable().default(null),
    appliquePar: idSchema.nullable().default(null),
    revision: revisionSchema,
}));
//# sourceMappingURL=schemas.js.map